/**
 * Admin Invite Codes page - manage invite codes
 */
import { useState, useMemo } from 'react'
import { useInviteCodes, useCreateInviteCode, useRevokeInviteCode, useTenants } from '../hooks'
import { Button, Input, Modal, LoadingCard } from '../components/common'
import type { InviteCodeCreate, Tenant } from '../types'

const STATUS_COLORS: Record<string, string> = {
  available: 'bg-green-100 text-green-800',
  used: 'bg-blue-100 text-blue-800',
  expired: 'bg-yellow-100 text-yellow-800',
  revoked: 'bg-red-100 text-red-800',
}

const STATUS_LABELS: Record<string, string> = {
  available: 'Available',
  used: 'Used',
  expired: 'Expired',
  revoked: 'Revoked',
}

interface AdminInvitesPageProps {
  embedded?: boolean
}

export function AdminInvitesPage({ embedded = false }: AdminInvitesPageProps) {
  const { data: tenants, isLoading: tenantsLoading } = useTenants()
  const [selectedTenantId, setSelectedTenantId] = useState<string>('')
  const { data: codes, isLoading: codesLoading } = useInviteCodes(selectedTenantId || undefined)
  const createCode = useCreateInviteCode()
  const revokeCode = useRevokeInviteCode()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [formData, setFormData] = useState<Partial<InviteCodeCreate>>({
    tenant_id: '',
    email: '',
    expires_in_days: 7,
    notes: '',
  })
  const [error, setError] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Map tenant IDs to names for display
  const tenantMap = useMemo(() => {
    const map: Record<string, Tenant> = {}
    tenants?.forEach((t) => {
      map[t.id] = t
    })
    return map
  }, [tenants])

  const openCreateModal = () => {
    // Default to selected tenant filter or first tenant
    const defaultTenantId = selectedTenantId || tenants?.[0]?.id || ''
    setFormData({ tenant_id: defaultTenantId, email: '', expires_in_days: 7, notes: '' })
    setError('')
    setIsModalOpen(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.tenant_id) {
      setError('Please select a tenant')
      return
    }

    try {
      // Clean up empty strings
      const data: InviteCodeCreate = {
        tenant_id: formData.tenant_id,
        expires_in_days: formData.expires_in_days,
      }
      if (formData.email?.trim()) {
        data.email = formData.email.trim()
      }
      if (formData.notes?.trim()) {
        data.notes = formData.notes.trim()
      }

      await createCode.mutateAsync(data)
      setIsModalOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create invite code')
    }
  }

  const handleRevoke = async (codeId: string) => {
    if (!confirm('Are you sure you want to revoke this invite code?')) return

    try {
      await revokeCode.mutateAsync(codeId)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to revoke invite code')
    }
  }

  const copyToClipboard = async (code: string, id: string) => {
    try {
      await navigator.clipboard.writeText(code)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      alert('Failed to copy to clipboard')
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const isLoading = tenantsLoading || codesLoading

  if (isLoading) {
    return <LoadingCard />
  }

  return (
    <div className="space-y-6">
      {!embedded ? (
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Invite Codes</h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage invite codes for new user signups
            </p>
          </div>
          <div className="flex items-center gap-4">
            <select
              className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
            >
              <option value="">All Tenants</option>
              {tenants?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <Button onClick={openCreateModal}>Generate Code</Button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end items-center gap-4">
          <select
            className="block px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
          >
            <option value="">All Tenants</option>
            {tenants?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <Button onClick={openCreateModal}>Generate Code</Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        {['available', 'used', 'expired', 'revoked'].map((status) => {
          const count = codes?.filter((c) => c.status === status).length || 0
          return (
            <div key={status} className="bg-white rounded-lg shadow p-4">
              <div className="text-2xl font-bold text-gray-900">{count}</div>
              <div className="text-sm text-gray-500 capitalize">{status}</div>
            </div>
          )
        })}
      </div>

      {/* Codes Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Code
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Tenant
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Expires
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Notes
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {codes?.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-8 text-center text-gray-500">
                  No invite codes yet. Generate one to get started.
                </td>
              </tr>
            ) : (
              codes?.map((code) => (
                <tr key={code.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                      {code.code}
                    </code>
                    <button
                      onClick={() => copyToClipboard(code.code, code.id)}
                      className="ml-2 text-gray-400 hover:text-gray-600"
                      title="Copy to clipboard"
                    >
                      {copiedId === code.id ? (
                        <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[code.status]}`}>
                      {STATUS_LABELS[code.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {tenantMap[code.tenant_id]?.name || code.tenant_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {code.status === 'used' && code.used_by_email ? (
                      <span title={`Used by: ${code.used_by_email}`}>
                        {code.used_by_email}
                      </span>
                    ) : code.email ? (
                      <span className="text-gray-900">{code.email}</span>
                    ) : (
                      <span className="text-gray-400">Any email</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(code.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(code.expires_at)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={code.notes || undefined}>
                    {code.notes || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    {code.status === 'available' && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRevoke(code.id)}
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Generate Invite Code"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tenant <span className="text-red-500">*</span>
            </label>
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={formData.tenant_id || ''}
              onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
              required
            >
              <option value="">Select a tenant</option>
              {tenants?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-sm text-gray-500">
              New user will be assigned to this tenant
            </p>
          </div>

          <Input
            label="Email (optional)"
            type="email"
            value={formData.email || ''}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="user@example.com"
            helperText="If set, only this email can use the code"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expires In
            </label>
            <select
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              value={formData.expires_in_days || 7}
              onChange={(e) => setFormData({ ...formData, expires_in_days: parseInt(e.target.value) || undefined })}
            >
              <option value={1}>1 day</option>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
              <option value="">Never (no expiration)</option>
            </select>
          </div>

          <Input
            label="Notes (optional)"
            value={formData.notes || ''}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="e.g., For John Smith"
          />

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={createCode.isPending}
            >
              Generate Code
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
